import AccountBoxIcon from '@mui/icons-material/AccountBox';
import CommentIcon from '@mui/icons-material/Comment';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    Grid,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Theme,
    Tooltip,
    Typography,
} from '@mui/material';
import dinero from 'dinero.js';
import moment from 'moment';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import useSextforceAutoCampaignTipReplyLogsBySubscriber from '../../../../hooks/useSextforceAutoCampaignTipReplyLogsBySubscriber';
import { TablePaginationActions } from '../../../../pages/services/messageUsers/MessageUsersAutoMassDm';
import StyledCardTitleBar from '../../../common/StyledCardTitleBar';

type Props = {
    open: boolean;
    onClose: () => void;
    theme: Theme;
};

const SextforceAutoCampaignTipReplyAllTippersListDialog = (props: Props) => {
    const { open, onClose, theme } = props;

    const [offset, setOffset] = useState<number>(0);
    const [limit, setLimit] = useState<number>(10);
    const tableTopRef = useRef<HTMLDivElement>(null);

    const { data: autoCampaignTipReplies, isLoading: isLoadingAutoCampaignReplies } = useSextforceAutoCampaignTipReplyLogsBySubscriber(
        offset,
        limit,
    );

    const handleClose = (event: {}, reason: 'backdropClick' | 'escapeKeyDown') => {
        if (reason && reason === 'backdropClick') {
            return;
        }

        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth disableEscapeKeyDown scroll="paper">
            <StyledCardTitleBar
                title={`Auto Tip Campaign Tippers${
                    !isLoadingAutoCampaignReplies &&
                    autoCampaignTipReplies &&
                    ` (${autoCampaignTipReplies.metadata.total.toLocaleString()})`
                }`}
            />
            <DialogContent>
                <DialogContentText>The following users have tipped on your campaign posts.</DialogContentText>
                <Grid container alignItems={'center'} justifyContent={'center'} spacing={1} sx={{ mt: 1 }}>
                    {isLoadingAutoCampaignReplies && (
                        <Grid item xs={12} textAlign="center">
                            <Stack direction={'column'} spacing={1} alignItems={'center'} justifyContent={'center'}>
                                <CircularProgress />
                                <Typography variant="body1">Loading...</Typography>
                            </Stack>
                        </Grid>
                    )}
                    {!isLoadingAutoCampaignReplies && autoCampaignTipReplies && autoCampaignTipReplies.metadata.total === 0 && (
                        <Grid item xs={12} textAlign="center">
                            <Typography variant="body1" color={theme.palette.text.disabled}>
                                No tippers found
                            </Typography>
                        </Grid>
                    )}
                    {!isLoadingAutoCampaignReplies && autoCampaignTipReplies && autoCampaignTipReplies.metadata.total > 0 && (
                        <Grid item xs={12}>
                            <Box ref={tableTopRef} sx={{ overflowX: 'auto' }}>
                                <Table
                                    width="100%"
                                    sx={{
                                        // no left/right padding on the first and last cells
                                        '& td:first-of-type, & th:first-of-type': {
                                            paddingLeft: 0,
                                        },
                                        // Bold header
                                        '& th': {
                                            fontWeight: 'bold',
                                        },
                                        // No wrapping
                                        whiteSpace: 'nowrap',
                                        // make last column sticky
                                        '& th, & td': {
                                            '&:last-child': {
                                                position: 'sticky',
                                                right: 0,
                                                backgroundColor: theme.palette.background.paper,
                                            },
                                        },
                                    }}
                                >
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Date</TableCell>
                                            <TableCell>Campaign</TableCell>
                                            <TableCell>User ID</TableCell>
                                            <TableCell>Username</TableCell>
                                            <TableCell>Name</TableCell>
                                            <TableCell>Tip</TableCell>
                                            <TableCell align="center">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {autoCampaignTipReplies.data.map(autoCampaignTipReply => (
                                            <TableRow key={autoCampaignTipReply.log._id}>
                                                <TableCell>{moment(autoCampaignTipReply.log.notificationDate).format('L LT')}</TableCell>
                                                <TableCell>{autoCampaignTipReply.name}</TableCell>
                                                <TableCell align="right">{autoCampaignTipReply.log.user.id}</TableCell>
                                                <TableCell>{autoCampaignTipReply.log.user.username}</TableCell>
                                                <TableCell>{autoCampaignTipReply.log.user.name}</TableCell>
                                                <TableCell align="right">
                                                    {dinero({
                                                        amount: Math.trunc(autoCampaignTipReply.log.tipAmount * 100),
                                                        currency: 'USD',
                                                    }).toFormat()}
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Stack direction={'row'} spacing={0.5}>
                                                        <Tooltip title="Go to message in OnlyFans chat" arrow placement="top">
                                                            <IconButton
                                                                component={Link}
                                                                to={
                                                                    autoCampaignTipReply.log.messageId
                                                                        ? `https://onlyfans.com/my/chats/chat/${autoCampaignTipReply.log.user.id}?firstId=${autoCampaignTipReply.log.messageId}`
                                                                        : ''
                                                                }
                                                                target="_blank"
                                                                disabled={!autoCampaignTipReply.log.messageId}
                                                                color="primary"
                                                            >
                                                                <CommentIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Go to user's profile" arrow placement="top">
                                                            <IconButton
                                                                component={Link}
                                                                to={`https://onlyfans.com/${autoCampaignTipReply.log.user.username}`}
                                                                target="_blank"
                                                                color="secondary"
                                                            >
                                                                <AccountBoxIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>
                            <TablePaginationActions
                                count={autoCampaignTipReplies.metadata.total}
                                limit={limit}
                                setLimit={setLimit}
                                skip={offset}
                                setSkip={setOffset}
                                tableTopRef={tableTopRef}
                            />
                        </Grid>
                    )}
                </Grid>
            </DialogContent>
            <DialogActions
                sx={{
                    backgroundColor: theme.palette.grey[100],
                }}
            >
                <Button
                    color="error"
                    size="medium"
                    onClick={() => {
                        handleClose({}, 'escapeKeyDown');
                    }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SextforceAutoCampaignTipReplyAllTippersListDialog;
